/**
 * Chat domain types.
 *
 * The shapes shared by the storage layer, the AI service layer and the chat
 * UI. Provider-specific shapes (Ollama's raw REST payloads) stay inside
 * `services/ai/providers/ollama` and get mapped into these before anything
 * else sees them.
 */

import type { KnowledgeScope } from "./knowledge";

export type ChatRole = "user" | "assistant" | "system";

export type MessageStatus = "complete" | "streaming" | "error" | "stopped";

/** An image attached to a message. `base64` carries no data-URI prefix —
 * that's the raw shape Ollama's `/api/chat` `images` field expects, so
 * `ollamaClient.ts` can forward it untouched. */
export interface ChatImageAttachment {
  readonly id: string;
  readonly base64: string;
  readonly mimeType: string;
}

/** A single message in a conversation. */
export interface ChatMessage {
  readonly id: string;
  readonly role: ChatRole;
  content: string;
  readonly createdAt: number;
  status: MessageStatus;
  /** Present when `status === "error"`. */
  error?: string;
  /** Vision attachments — optional so every existing text-only message
   * literal still type-checks unchanged. */
  images?: ChatImageAttachment[];
  /** Starred via the message's favorite-star control. */
  favorite?: boolean;
}

/** Per-conversation generation parameters — placeholders wired to Ollama's
 * `options` object. */
export interface ChatParams {
  temperature: number;
  /** Context window size (Ollama's `num_ctx`). */
  numCtx: number;
  topP: number;
  /** Max tokens to generate (Ollama's `num_predict`). `-1` means unlimited. */
  numPredict: number;
}

export const DEFAULT_CHAT_PARAMS: ChatParams = {
  temperature: 0.8,
  numCtx: 4096,
  topP: 0.9,
  numPredict: -1,
};

/** A full conversation, as persisted by the storage layer. */
export interface Conversation {
  readonly id: string;
  title: string;
  readonly createdAt: number;
  updatedAt: number;
  /** Id of the model this conversation was created with / continues to use. */
  modelId: string | null;
  params: ChatParams;
  messages: ChatMessage[];
  /** True once auto-title generation has run (or been skipped after failing). */
  titleGenerated: boolean;
  /** Which slice of the Knowledge library this conversation draws on for
   * RAG context. Defaults to `{ mode: "none" }` — see `types/knowledge.ts`. */
  knowledgeScope: KnowledgeScope;
  /** The workspace this conversation belongs to. `null` only for
   * pre-Phase-4 rows not yet backfilled. */
  workspaceId: string | null;
  pinned: boolean;
  folderId: string | null;
  /** Set when this conversation was created via "Branch" from another one. */
  parentConversationId: string | null;
  /** The message `seq` in the parent conversation the branch forked from. */
  branchPointSeq: number | null;
}

/** A folder grouping conversations within a workspace — organizational
 * only, doesn't affect message content or storage location. */
export interface ConversationFolder {
  readonly id: string;
  workspaceId: string;
  name: string;
  readonly createdAt: number;
}

/** Lightweight summary used for sidebar lists and command-palette search —
 * avoids loading every conversation's full message history up front. */
export interface ConversationSummary {
  readonly id: string;
  title: string;
  readonly createdAt: number;
  updatedAt: number;
  modelId: string | null;
  messageCount: number;
  workspaceId: string | null;
  pinned: boolean;
  folderId: string | null;
}

/** A locally installed model, normalised from a provider's raw listing. */
export interface ModelInfo {
  readonly id: string;
  readonly name: string;
  /** e.g. "7B", "13B" — undefined when the provider doesn't report it. */
  readonly paramSize?: string;
  readonly sizeBytes: number;
  readonly modifiedAt: number;
  readonly available: boolean;
  /** Whether Ollama reports this model as multimodal (its `details.families`
   * includes `"clip"`) — undefined when the provider doesn't report
   * families at all, which callers should treat as "unknown", not "no". */
  readonly supportsVision?: boolean;
}

/** Readiness of the local Ollama service. */
export type OllamaConnectionStatus =
  | "checking"
  | "not-installed"
  | "not-running"
  | "ready"
  | "error";

export interface OllamaStatus {
  installed: boolean;
  running: boolean;
  version: string | null;
}
